import React, {useEffect, useState} from 'react';
import {Button, NavBar} from 'antd-mobile';
import {getNavBarContainerStyle, getBottomNavBarContainerStyle} from "../../../utils/deviceUtils";
import {ArrowLeftOutlined} from '@ant-design/icons';
import {useLocation, useNavigate} from 'react-router-dom';
import {playScanFailedSound, playScanSound} from '../../../utils/scanUtils';
import {PickupInfoPanelProps} from '../../../components/PickupInfoPanel';
import {IncompletePromptPath, TaskPath} from "../../../enums/DeliveryNavigatePathEnum";
import {BarCodeScanner, useBarCodeScanner} from '../../../components/BarCodeScanner';
import {isTrackingNumber} from '../../../utils/BarCodeUtil';
import {parseExceptionMessageKey, showErrorNoTitle, showRepeatedScanError} from '../../../utils/exceptionUtils';
import {useDeliveryTaskOrderOperations} from '../store/deliveryStore';
import {DeliveryTaskOrderType} from '../graphql/types/DeliveryTaskOrderType';
import {useLocationStore} from "../../../store/locationStore";
import {t} from "../../../i18n";
import {useLazyQuery, useQuery} from "@apollo/client";
import {formatStopNumber, sortDeliveryOrders} from "../../../utils/stopNumberUtils";
import {
    GET_DELIVERY_TASK_ORDER_BY_TRACKING_NUMBER,
    GetDeliveryTaskOrderByTrackingNumberResponse
} from "../graphql/queries/getDeliveryTaskOrderByTrackingNumber";
import {FileOutline} from "antd-mobile-icons";
import {CHECK_SAME_AREA_ENABLED, CheckSameAreaEnabledResponse} from "../graphql/queries/checkSameAreaEnabled";
import {
    DeliveryTaskOrderListResponse,
    GET_DELIVERY_TASK_ORDER_LIST
} from "../graphql/queries/deliveryTaskOrderList";

export interface scanDataInfo {
    trackingNumber: string;
    stopNumber: string;
    subStopNumber?: string | null;
    routeName: string;
    driverName?: string;
}

interface PageBodyProps {
    checkSameAreaEnabled?: boolean;
}

const PageBody: React.FC<PageBodyProps> = ({ checkSameAreaEnabled }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [lastScannedItem, setLastScannedItem] = useState<PickupInfoPanelProps | null>(null);
    const {position} = useLocationStore();
    const [orderTotal, setOrderTotal] = useState<number>(0);
    const [trackingNumbers, setTrackingNumbers] = useState<string[]>([]);
    const [newAssignmentsData, setNewAssignmentsData] = useState<DeliveryTaskOrderType[]>([]);
    const [pickupVersion, setPickupVersion] = useState<number>(0);
    const {handleNewAssignmentScan, handleTransferInScan} = useDeliveryTaskOrderOperations();
    const {scanResult, setIsVisible} = useBarCodeScanner();
    const { data: pickupData, refetch: refetchDeliveryTaskOrderList } = useQuery<DeliveryTaskOrderListResponse>(
        GET_DELIVERY_TASK_ORDER_LIST,
        {
            variables: {
                maxDeliveryTaskId: pickupVersion,
            },
            fetchPolicy: 'network-only'
        }
    );
    const deliveringTasks = pickupData?.getDeliveryTaskOrderList?.deliveryTaskOrderList || [];
    // 从 location.state 获取初始标签，默认为 'scanned'
    const initialTab = (location.state as { activeTab?: 'scanned' | 'unscanned' })?.activeTab || 'scanned';
    const [activeTab, setActiveTab] = useState<'scanned' | 'unscanned'>(initialTab);
    
    // 使用 useLazyQuery 来处理按需查询
    const [getDeliveryTaskOrderByTrackingNumber, { data: trackingData, loading: trackingLoading }] = useLazyQuery<GetDeliveryTaskOrderByTrackingNumberResponse>(
        GET_DELIVERY_TASK_ORDER_BY_TRACKING_NUMBER
    );

    // 当 location.state 变化时，更新 activeTab
    useEffect(() => {
        const stateTab = (location.state as { activeTab?: 'scanned' | 'unscanned' })?.activeTab;
        if (stateTab) {
            setActiveTab(stateTab);
        }
    }, [location.state]);

    useEffect(() => {
        setIsVisible(true);
        return () => {
            setIsVisible(false);
        }
    }, []);

    useEffect(() => {
        const response = pickupData?.getDeliveryTaskOrderList;
        if (!response) {
            return;
        }
        if (response.maxDeliveryTaskId !== pickupVersion) {
            setPickupVersion(response.maxDeliveryTaskId);
        }
    }, [pickupData?.getDeliveryTaskOrderList]);

    useEffect(() => {
        if (!deliveringTasks.length) return;
        
        const newAssignments = deliveringTasks.filter(item => {
            const order = item?.orderWithAddress?.order;
            // 875: 新分配包裹和转入包裹统一展示在新分配入口
            return ((item?.isNewAssignment || item?.isTransferIn) && !item?.isDelivery && !order?.isAbnormal && !item?.isTransferOut && !order?.isFinished);
        })
            .sort((a, b) => sortDeliveryOrders({
                deliveryTaskId: a.deliveryTaskId,
                routeHistoryId: a.deliveryTaskType?.routeHistoryId || 0,
                routeName: a.routeName,
                stopNumber: a.stopNumber,
                subStopNumber: a.subStopNumber
            }, {
                deliveryTaskId: b.deliveryTaskId,
                routeHistoryId: b.deliveryTaskType?.routeHistoryId || 0,
                routeName: b.routeName,
                stopNumber: b.stopNumber,
                subStopNumber: b.subStopNumber
            }, checkSameAreaEnabled)) || [];

        if (!newAssignments.length) return;
        const scannedTrackingNumbers = newAssignments
            .filter(item => item.isPickedUp)
            .map(item => item.trackingNumber);

        setTrackingNumbers(scannedTrackingNumbers);
        setNewAssignmentsData(newAssignments);
        setOrderTotal(newAssignments.length);
    }, [deliveringTasks, checkSameAreaEnabled]);

    useEffect(() => {
        if (scanResult) {
            handleScanSuccess(scanResult);
        }
    }, [scanResult]);

    // 处理扫描成功
    const handleScanSuccess = async (id: string) => {
        if (id === lastScannedItem?.id) {
            return;
        }
        if (!isTrackingNumber(id)) {
            return;
        }
        const orderData = newAssignmentsData.find(item => item.trackingNumber === id);
        try {
            let result: DeliveryTaskOrderType | undefined;
            // 875: 新分配入口统一走 newAssignmentScan，转入包裹不再自动发车，等发车按钮统一处理
            result = await handleNewAssignmentScan({
                trackingNumber: id,
                scanLocation: {
                    latitude: position?.latitude || 0.0,
                    longitude: position?.longitude || 0.0
                }
            });
            if (result) {
                await playScanSound();
                setLastScannedItem({
                    id,
                    status: 'success',
                    item: {
                        trackingNumber: result.trackingNumber,
                        stopNumber: result.stopNumber.toString(),
                        subStopNumber: result.subStopNumber,
                        routeName: result.routeName,
                    }
                });
                // 校验运单号是否存在于当前司机的新任务数据中
                const isInNewAssignments = newAssignmentsData.some(item => item.trackingNumber === id);
                if (result && !trackingNumbers.includes(result.trackingNumber) && isInNewAssignments) {
                    setTrackingNumbers(prev => [...prev, result?.trackingNumber || '']);
                }
                // 刷新数据以获取最新的 lastDeliveryScanTime
                await refetchDeliveryTaskOrderList({ maxDeliveryTaskId: pickupVersion });
            } else {
                showErrorNoTitle('Wrong Package');
                await playScanFailedSound();
            }
        } catch (error) {
            console.error('handleScanSuccess error:', error);
            const messageKey = parseExceptionMessageKey(error);
            if (messageKey === "errors.business.alreadyScanned") {
                const isTransferInFlag = (orderData?.isTransferIn) ? true: false;
                showRepeatedScanError(error);
                // 通过trackingNumber调一个查询接口，给LastScannedItem赋值。
                getDeliveryTaskOrderByTrackingNumber({
                    variables: { trackingNumber: id , isTransferIn : isTransferInFlag}
                }).then(({ data }) => {
                    if (data?.getDeliveryTaskOrderByTrackingNumber) {
                        const result = data.getDeliveryTaskOrderByTrackingNumber;
                        setLastScannedItem({
                            id,
                            status: 'success',
                            item: {
                                trackingNumber: result.trackingNumber,
                                stopNumber: result.stopNumber.toString(),
                                subStopNumber: result.subStopNumber,
                                routeName: result.routeName,
                            }
                        });
                    }
                });
            } else {
                showErrorNoTitle(error);
                await playScanFailedSound();
            }
        }
    };

    const handleStartDelivery = () => {
        navigate(IncompletePromptPath.DELIVERY_NEW_ASSIGNMENT_START_CONFIRM);
    };

    // 计算tab数据
    const scannedList = newAssignmentsData
        .filter(item => trackingNumbers.includes(item.trackingNumber))
        .sort((a, b) => {
            const timeA = a.orderWithAddress?.order?.lastDeliveryScanTime ?? 0;
            const timeB = b.orderWithAddress?.order?.lastDeliveryScanTime ?? 0;
            // 降序排序：时间戳大的（最新的）排在前面
            return timeB - timeA;
        });

    
    const unscannedList = newAssignmentsData.filter(item => !trackingNumbers.includes(item.trackingNumber));

    return (
        <div className="flex flex-col h-screen bg-white overflow-hidden">
            {lastScannedItem && lastScannedItem.item && (
                <div className="bg-[#e6f0fd] px-4 py-2">
                    <div className="text-base text-black font-medium mb-1 text-center">
                        <span className="font-medium">
                            {t('pages.delivery.detail.trackingNumber')}
                        </span>:
                        <p className="font-medium break-all">
                            {lastScannedItem.item.trackingNumber}
                         </p>
                    </div>
                    <div className="flex flex-row">
                        <div className="flex-1 flex flex-col items-center justify-center">
                            <span
                                className="text-3xl font-bold text-green-600 leading-none">{lastScannedItem.item.routeName}</span>
                            <span className="text-xs text-green-600 mt-1">{t('pages.delivery.detail.routeNo')}</span>
                        </div>
                        <div className="flex-1 flex flex-col items-center justify-center">
                            {(() => {
                                const stopNumber = formatStopNumber(parseInt(lastScannedItem.item.stopNumber), lastScannedItem.item.subStopNumber);
                                return (
                                    <span className={`${stopNumber.length > 4 ? 'text-2xl' : 'text-3xl'} font-bold text-blue-600 leading-none whitespace-nowrap`}>
                                        {stopNumber}
                                    </span>
                                );
                            })()}
                            <span className="text-xs text-blue-600 mt-1">{t('pages.delivery.detail.stopNo')}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* tab切换区 */}
            <div className="flex flex-row bg-white border-b border-gray-200">
                <div
                    className={`flex-1 text-center py-2 cursor-pointer ${activeTab === 'scanned' ? 'text-blue-600 font-bold border-b-2 border-blue-600' : 'text-gray-400'}`}
                    onClick={() => setActiveTab('scanned')}
                >
                    {t('pages.scanned')}({scannedList.length})
                </div>
                <div
                    className={`flex-1 text-center py-2 cursor-pointer ${activeTab === 'unscanned' ? 'text-blue-600 font-bold border-b-2 border-blue-600' : 'text-gray-400'}`}
                    onClick={() => setActiveTab('unscanned')}
                >
                    {t('pages.unScanned')}({unscannedList.length})
                </div>
            </div>

            {/* 列表区，滚动且不会遮挡按钮 */}
            <div className="flex-1 min-h-0 overflow-y-auto bg-white">
                {(activeTab === 'scanned' ? scannedList : unscannedList).map((item, idx) => {
                    const order = item.orderWithAddress?.order;
                    const isRedelivery = order?.deliveryAttempts > 0 || order?.nextAction === 'ReDeliver';
                    const isTransferIn = item?.isTransferIn ?? false;

                    return (
                        <div key={item.trackingNumber + idx}
                             className={`flex flex-col px-4 py-2 border-b ${isTransferIn ? 'bg-[#EDF5FF]' : isRedelivery ? 'bg-[#fffbe6]' : ''}`}>
                            <div className="flex flex-row items-center">
                                <div className="flex-1 text-base break-all">{item.trackingNumber}</div>
                                <div className="w-20 text-center break-words">{item.routeName}</div>
                                <div className="w-16 text-center break-words">{formatStopNumber(item.stopNumber, item.subStopNumber)}</div>
                            </div>
                            {isTransferIn && (
                                <div className="text-xs mt-1">{t('pages.transfer')}</div>
                            )}
                            {isRedelivery && !isTransferIn && (
                                <div className="text-xs mt-1">{t('pages.reDeliver')}</div>
                            )}
                        </div>
                    );
                })}
            </div>
            {/* 底部按钮，始终可见 */}
            <div {...getBottomNavBarContainerStyle('sticky bottom-0 left-0 w-full p-4 bg-white z-20')}>
                <Button
                    onClick={handleStartDelivery}
                    color="primary"
                    className="w-full rounded-lg text-lg font-bold"
                >
                    {t('pages.completeScan')}
                </Button>
            </div>
        </div>
    );
};

const NewAssignmentsScanPage: React.FC = () => {
    const navigate = useNavigate();
    
    // 查询是否启用同区域校验
    const { data: sameAreaEnabledData } = useQuery<CheckSameAreaEnabledResponse>(
        CHECK_SAME_AREA_ENABLED,
        {
            fetchPolicy: 'network-only'
        }
    );
    
    const showAreaScanButton = sameAreaEnabledData?.checkSameAreaEnabled === true;
    
    return (
        <div className="flex flex-col h-screen bg-white overflow-hidden">
            <div {...getNavBarContainerStyle()}>
                <NavBar
                    onBack={() => navigate(TaskPath.DELIVERY_TASK)}
                    backIcon={<ArrowLeftOutlined/>}
                    right={
                        showAreaScanButton ? (
                            <Button
                                fill='none'
                                onClick={() => navigate(TaskPath.DELIVERY_TASK_AREA_SCAN_DETAIL)}
                                style={{ padding: '5px', fontSize: '25px' }}
                            >
                                <FileOutline />
                            </Button>
                        ) : null
                    }
                    className="font-bold text-lg justify-center"
                >
                    {t('pages.delivery.task.newTitle')}
                </NavBar>
            </div>
            <BarCodeScanner/>
            <PageBody checkSameAreaEnabled={sameAreaEnabledData?.checkSameAreaEnabled} />
        </div>
    );
};

export default NewAssignmentsScanPage;
